interface FooterMenuToggleConfig {
	readonly selector: string;
	readonly label: string;
	readonly controlsId: string;
}

const MOBILE_HEADER_QUERY = '(max-width: 768px)';
const MOBILE_DRAWER_ID = 'ofertelio-mobile-navigation';
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled])';

const FOOTER_MENU_TOGGLES: readonly FooterMenuToggleConfig[] = [
	{
		selector: '.ofertelio-footer-toggle-explore .elementor-menu-toggle',
		label: 'Explorar',
		controlsId: 'ofertelio-footer-menu-explore',
	},
	{
		selector: '.ofertelio-footer-toggle-legal .elementor-menu-toggle',
		label: 'Legal',
		controlsId: 'ofertelio-footer-menu-legal',
	},
];

/**
 * Elementor Pro's Nav Menu widget renders its mobile toggle with a generic,
 * plugin-wide "Menu Toggle" aria-label and no aria-controls (the dropdown
 * panel is never given an id to point at). Both are set here as a post-render
 * enhancement, since the widget markup itself comes from plugin code.
 */
function initFooterMenuToggles(): void {
	FOOTER_MENU_TOGGLES.forEach( ( { selector, label, controlsId } ) => {
		const toggle = document.querySelector<HTMLElement>( selector );
		if ( ! toggle || ! toggle.parentElement ) {
			return;
		}

		toggle.setAttribute( 'aria-label', label );

		const panel = toggle.parentElement.querySelector<HTMLElement>( '.elementor-nav-menu--dropdown' );
		if ( panel ) {
			panel.id = controlsId;
			toggle.setAttribute( 'aria-controls', controlsId );
		}
	} );
}

function updateMarket( value: string ): void {
	const url = new URL( window.location.href );

	if ( value === '' ) {
		url.searchParams.delete( 'ofertelio_market' );
	} else {
		url.searchParams.set( 'ofertelio_market', value );
	}

	window.location.assign( url.toString() );
}

/**
 * The header's market/country selector has no server-rendered form — selecting
 * an option reloads the current page with ?ofertelio_market={code} set, which
 * DealsQuery::args_from_atts() (plugin) picks up as the active market filter.
 */
function initMarketSelector(): void {
	const select = document.getElementById( 'ofertelio-market-select' ) as HTMLSelectElement | null;

	if ( ! select ) {
		return;
	}

	select.addEventListener( 'change', () => updateMarket( select.value ) );
}

function initResponsiveFilterPanels(): void {
	const panels = document.querySelectorAll<HTMLDetailsElement>( '.ofertelio-filter-panel' );
	if ( ! panels.length ) {
		return;
	}

	const mobileMedia = window.matchMedia( MOBILE_HEADER_QUERY );
	const updatePanels = (): void => {
		panels.forEach( ( panel ) => {
			if ( mobileMedia.matches ) {
				if ( ! panel.querySelector( '.ofertelio-active-filters' ) ) {
					panel.removeAttribute( 'open' );
				}
			} else {
				panel.setAttribute( 'open', '' );
			}
		} );
	};

	updatePanels();
	mobileMedia.addEventListener( 'change', updatePanels );
}

/**
 * Elementor's tablet dropdown mixes the menu, search, and market selector in
 * the same flex row. A dedicated mobile drawer keeps the desktop widget as the
 * source of truth while providing a compact, accessible mobile experience.
 */
function initMobileHeaderMenu(): void {
	const header = document.querySelector<HTMLElement>( '.elementor-location-header.elementor-32' );
	const headerContainer = header?.querySelector<HTMLElement>( '.elementor-element-8194617' );
	const logoLink = header?.querySelector<HTMLAnchorElement>( '.elementor-element-8194280 a' );
	const sourceLinks = header?.querySelectorAll<HTMLAnchorElement>( '.elementor-nav-menu--main .elementor-item' );
	const sourceSearch = header?.querySelector<HTMLFormElement>( '.elementor-search-form' );
	const sourceSearchInput = sourceSearch?.querySelector<HTMLInputElement>( 'input[type="search"]' );
	const sourceMarket = header?.querySelector<HTMLSelectElement>( '#ofertelio-market-select' );

	if ( ! header || ! headerContainer || ! logoLink || ! sourceLinks?.length || ! sourceSearch || ! sourceSearchInput ) {
		return;
	}

	const menuButton = document.createElement( 'button' );
	menuButton.type = 'button';
	menuButton.className = 'ofertelio-mobile-menu-button';
	menuButton.setAttribute( 'aria-label', 'Abrir menú' );
	menuButton.setAttribute( 'aria-controls', MOBILE_DRAWER_ID );
	menuButton.setAttribute( 'aria-expanded', 'false' );
	menuButton.innerHTML = '<span aria-hidden="true"></span><span aria-hidden="true"></span><span aria-hidden="true"></span>';
	headerContainer.append( menuButton );

	const drawer = document.createElement( 'div' );
	drawer.id = MOBILE_DRAWER_ID;
	drawer.className = 'ofertelio-mobile-nav';
	drawer.hidden = true;
	drawer.setAttribute( 'aria-hidden', 'true' );
	drawer.innerHTML = `
		<button class="ofertelio-mobile-nav__backdrop" type="button" tabindex="-1" aria-label="Cerrar menú"></button>
		<aside class="ofertelio-mobile-nav__panel" role="dialog" aria-modal="true" aria-label="Navegación principal">
			<div class="ofertelio-mobile-nav__topbar">
				<a class="ofertelio-mobile-nav__brand" href="/">Ofertelio</a>
				<button class="ofertelio-mobile-nav__close" type="button" aria-label="Cerrar menú"><span aria-hidden="true"></span></button>
			</div>
			<nav class="ofertelio-mobile-nav__links" aria-label="Navegación principal"></nav>
			<div class="ofertelio-mobile-nav__tools"></div>
		</aside>
	`;

	const mobileLinks = drawer.querySelector<HTMLElement>( '.ofertelio-mobile-nav__links' );
	const tools = drawer.querySelector<HTMLElement>( '.ofertelio-mobile-nav__tools' );
	const closeButton = drawer.querySelector<HTMLButtonElement>( '.ofertelio-mobile-nav__close' );
	const backdrop = drawer.querySelector<HTMLButtonElement>( '.ofertelio-mobile-nav__backdrop' );
	const mobileBrand = drawer.querySelector<HTMLAnchorElement>( '.ofertelio-mobile-nav__brand' );

	if ( ! mobileLinks || ! tools || ! closeButton || ! backdrop || ! mobileBrand ) {
		return;
	}

	mobileBrand.href = logoLink.href;

	const linksList = document.createElement( 'ul' );
	linksList.className = 'ofertelio-mobile-nav__list';

	sourceLinks.forEach( ( sourceLink ) => {
		const item = document.createElement( 'li' );
		const link = document.createElement( 'a' );
		link.href = sourceLink.href;
		link.textContent = sourceLink.textContent?.trim() ?? '';

		if ( sourceLink.getAttribute( 'aria-current' ) ) {
			link.setAttribute( 'aria-current', sourceLink.getAttribute( 'aria-current' ) ?? 'page' );
		}

		item.append( link );
		linksList.append( item );
	} );

	mobileLinks.append( linksList );

	const searchForm = document.createElement( 'form' );
	searchForm.className = 'ofertelio-mobile-nav__search';
	searchForm.role = 'search';
	searchForm.method = 'get';
	searchForm.action = sourceSearch.action;
	searchForm.innerHTML = `
		<label for="ofertelio-mobile-search">Buscar en Ofertelio</label>
		<div>
			<input id="ofertelio-mobile-search" name="s" type="search" autocomplete="off" placeholder="Ofertas, tiendas o guías…">
			<button type="submit">Buscar</button>
		</div>
	`;
	const mobileSearchInput = searchForm.querySelector<HTMLInputElement>( '#ofertelio-mobile-search' );
	if ( mobileSearchInput ) {
		mobileSearchInput.value = sourceSearchInput.value;
	}
	tools.append( searchForm );

	if ( sourceMarket ) {
		const marketLabel = document.createElement( 'label' );
		marketLabel.className = 'ofertelio-mobile-nav__market';
		marketLabel.innerHTML = '<span>Mercado</span>';

		const marketSelect = sourceMarket.cloneNode( true ) as HTMLSelectElement;
		marketSelect.id = 'ofertelio-mobile-market-select';
		marketSelect.removeAttribute( 'aria-label' );
		marketSelect.addEventListener( 'change', () => updateMarket( marketSelect.value ) );
		marketLabel.append( marketSelect );
		tools.append( marketLabel );
	}

	document.body.append( drawer );

	const mobileMedia = window.matchMedia( MOBILE_HEADER_QUERY );
	let closeTimer: number | undefined;

	const closeMenu = ( restoreFocus = true, immediate = false ): void => {
		window.clearTimeout( closeTimer );
		drawer.classList.remove( 'is-open' );
		drawer.setAttribute( 'aria-hidden', 'true' );
		menuButton.setAttribute( 'aria-expanded', 'false' );
		menuButton.setAttribute( 'aria-label', 'Abrir menú' );
		document.body.classList.remove( 'ofertelio-mobile-menu-open' );

		const finishClose = (): void => {
			drawer.hidden = true;
			if ( restoreFocus && mobileMedia.matches ) {
				menuButton.focus();
			}
		};

		if ( immediate ) {
			finishClose();
		} else {
			closeTimer = window.setTimeout( finishClose, 240 );
		}
	};

	const openMenu = (): void => {
		if ( ! mobileMedia.matches ) {
			return;
		}

		window.clearTimeout( closeTimer );
		drawer.hidden = false;
		drawer.setAttribute( 'aria-hidden', 'false' );
		menuButton.setAttribute( 'aria-expanded', 'true' );
		menuButton.setAttribute( 'aria-label', 'Cerrar menú' );
		document.body.classList.add( 'ofertelio-mobile-menu-open' );

		window.requestAnimationFrame( () => {
			drawer.classList.add( 'is-open' );
			closeButton.focus();
		} );
	};

	menuButton.addEventListener( 'click', () => {
		if ( menuButton.getAttribute( 'aria-expanded' ) === 'true' ) {
			closeMenu();
		} else {
			openMenu();
		}
	} );
	closeButton.addEventListener( 'click', () => closeMenu() );
	backdrop.addEventListener( 'click', () => closeMenu() );
	mobileLinks.addEventListener( 'click', ( event ) => {
		if ( ( event.target as HTMLElement ).closest( 'a' ) ) {
			closeMenu( false, true );
		}
	} );

	drawer.addEventListener( 'keydown', ( event ) => {
		if ( event.key === 'Escape' ) {
			event.preventDefault();
			closeMenu();
			return;
		}

		if ( event.key !== 'Tab' ) {
			return;
		}

		const focusableElements = Array.from( drawer.querySelectorAll<HTMLElement>( FOCUSABLE_SELECTOR ) )
			.filter( ( element ) => element.tabIndex >= 0 && element.offsetParent !== null );
		const first = focusableElements[ 0 ];
		const last = focusableElements[ focusableElements.length - 1 ];

		if ( ! first || ! last ) {
			return;
		}

		if ( event.shiftKey && document.activeElement === first ) {
			event.preventDefault();
			last.focus();
		} else if ( ! event.shiftKey && document.activeElement === last ) {
			event.preventDefault();
			first.focus();
		}
	} );

	mobileMedia.addEventListener( 'change', ( event ) => {
		if ( ! event.matches ) {
			closeMenu( false, true );
		}
	} );
}

document.addEventListener( 'DOMContentLoaded', initFooterMenuToggles );
document.addEventListener( 'DOMContentLoaded', initMarketSelector );
document.addEventListener( 'DOMContentLoaded', initResponsiveFilterPanels );
document.addEventListener( 'DOMContentLoaded', initMobileHeaderMenu );

export {};
